import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Section } from "./Privacy";

const LAST_UPDATED = "June 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-bone-100">
      <header className="border-b border-bone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-2xl text-forest-700">Toothpix</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="terms-page">
        <div className="border border-clay-600/40 bg-clay-400/10 text-clay-600 text-sm px-4 py-3 mb-10">
          <b>Draft, pending legal review.</b> This page is a plain-language draft and has not yet been reviewed by a lawyer.
        </div>

        <div className="overline mb-3">Terms</div>
        <h1 className="font-heading text-4xl md:text-5xl text-forest-700">Terms of Service</h1>
        <p className="text-sm text-forest-400 mt-3">Last updated: {LAST_UPDATED}</p>
        <p className="text-forest-500 mt-6 leading-relaxed">
          Toothpix is operated by <b>Zahar Lab</b> ("we", "us"). By creating an account or using
          Toothpix, you agree to these terms. We've kept them short and in plain language.
        </p>

        <Section title="What Toothpix is">
          <p>
            Toothpix gives orthodontic clinics a branded page and widget that generate an
            AI preview of what a patient's smile could look like after treatment, and captures
            that patient as a lead with automatic follow-up.
          </p>
        </Section>

        <Section title="Previews are illustrative, not a medical promise">
          <p>
            AI-generated smile previews are for illustration and marketing only. They are not a
            diagnosis, a treatment plan, or a guarantee of any clinical outcome. Only a licensed
            clinician can determine whether a treatment is appropriate and what results are
            realistic.
          </p>
        </Section>

        <Section title="Your responsibilities as a clinic">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Use the tool honestly and comply with the advertising and healthcare rules that apply to you.</li>
            <li>Only name treatments you are legally allowed to offer and advertise, and set your own treatment wording accordingly.</li>
            <li>Handle the patient leads you receive in line with applicable privacy laws.</li>
          </ul>
        </Section>

        <Section title="Billing">
          <p>
            Paid plans are billed monthly. Free trials and pilots convert to paid only if you
            choose to upgrade. You can cancel at any time; your access continues until the end of
            the current billing period.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>
            Don't upload photos you don't have permission to use, and don't use Toothpix to
            mislead patients. We may suspend accounts that misuse the service.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these terms? Email{" "}
            <a href="mailto:hello@toothpix.ai" className="text-forest-600 underline underline-offset-4">
              hello@toothpix.ai
            </a>{" "}
            (Zahar Lab).
          </p>
        </Section>
      </main>

      <footer className="border-t border-bone-200 py-10 px-6">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between gap-4 text-xs text-forest-400">
          <div>© {new Date().getFullYear()} Toothpix. All rights reserved. · A Zahar Lab product</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-forest-700">Privacy</Link>
            <Link to="/terms" className="hover:text-forest-700">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
